import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  FiMenu,
  FiUser,
  FiLogOut,
  FiPackage,
  FiFileText,
  FiMusic,
  FiBarChart,
  FiShoppingCart,
} from "react-icons/fi";
import { FaEnvelopeOpenText, FaGuitar, FaNewspaper } from "react-icons/fa";
import { TbTruckDelivery } from "react-icons/tb";
import { IoClose } from "react-icons/io5";
import { BiSupport } from "react-icons/bi";

import { ReactComponent as Logo } from "../../assets/icons/logoMrc.svg";

import { logout } from "../../store/slices/auth/asyncThunks";
import { getLowStockProducts } from "../../store/slices/products/asyncThunks";
import { getOrders } from "../../store/slices/orders/asyncThunks";
import { selectUser } from "../../store/slices/auth/slice";
import { selectPendingOrders } from "../../store/slices/orders/slice";

import supportChatService from "../../services/supportChatService";

import { toast } from "../modern-ui/sonner";
import useScrollLock from "../../hooks/useScrollLock";

const navItems = [
  {
    id: "dashboard",
    label: "Kontrolna Tabla",
    icon: FiBarChart,
    to: "/admin-panel/kontrolna-tabla",
  },
  {
    id: "band-analytics",
    label: "Analitika Bendova",
    icon: FaGuitar,
    to: "/admin-panel/analitika",
  },
  {
    id: "reports",
    label: "Mesečni Izveštaji",
    icon: FiFileText,
    to: "/admin-panel/izvestaji",
  },
  {
    id: "bands",
    label: "Bendovi",
    icon: FiMusic,
    to: "/admin-panel/bendovi",
  },
  {
    id: "categories",
    label: "Kategorije",
    icon: FiShoppingCart,
    to: "/admin-panel/kategorije",
  },
  {
    id: "products",
    label: "Proizvodi",
    icon: FiPackage,
    to: "/admin-panel/proizvodi",
  },
  {
    id: "orders",
    label: "Porudzbine",
    icon: TbTruckDelivery,
    to: "/admin-panel/porudzbine",
  },
  {
    id: "news",
    label: "Novosti",
    icon: FaNewspaper,
    to: "/admin-panel/novosti",
  },
  {
    id: "newsletters",
    label: "Newsletters",
    icon: FaEnvelopeOpenText,
    to: "/admin-panel/newsletters",
  },
];

const AdminContainer = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hideContent, setHideContent] = useState(false);

  // Get low stock products from Redux state
  const { lowStockProducts } = useSelector((state) => state.productsInfo);
  const hasLowStockAlerts =
    lowStockProducts?.data && lowStockProducts.data.length > 0;

  // Get pending orders from Redux state
  const pendingOrders = useSelector(selectPendingOrders);
  const hasPendingOrders = pendingOrders?.data && pendingOrders.data.length > 0;


  // Get user from Redux state
  const user = useSelector(selectUser);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  useEffect(() => {
    if (sidebarCollapsed) {
      setTimeout(() => {
        setHideContent(true);
      }, 250);
    } else {
      setHideContent(false);
    }
  }, [sidebarCollapsed]);

  const changePage = (page) => {
    navigate(page);

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        toast.info("Uspešno ste izlogovali sa naloga.");
      })
      .catch((err) => console.log(err.message));
  };

  // Fetch low stock products and orders on component mount
  useEffect(() => {
    dispatch(getLowStockProducts());
    dispatch(getOrders({ page: 1, limit: 100 })); // Fetch more orders to check for pending ones

    // Set interval to fetch every 15 minutes
    const interval = setInterval(() => {
      dispatch(getLowStockProducts());
      dispatch(getOrders({ page: 1, limit: 100 }));
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(interval);
  }, [dispatch]);

  // Connect admin to support chat when entering admin panel
  useEffect(() => {
    if (!user || !user.id) return;

    // Connect to support chat service if not already connected
    if (!supportChatService.isSocketConnected()) {
      console.log("AdminContainer: Connecting admin to support chat service");
      
      supportChatService.connect({
        id: user.id,
        userType: user.role === 'admin' ? 'admin' : 'band',
        username: user.username || user.name,
        email: user.email || '',
      });
    }

    // Cleanup - disconnect when admin leaves admin panel
    return () => {
      if (supportChatService.isSocketConnected()) {
        console.log("AdminContainer: Disconnecting admin from support chat service");
        supportChatService.disconnect();
      }
    };
  }, [user]);

  useScrollLock(sidebarCollapsed);

  return (
    <Container>
      <SidebarToggle $collapsed={sidebarCollapsed} onClick={toggleSidebar}>
        {!sidebarCollapsed ? <FiMenu /> : <IoClose />}
      </SidebarToggle>

      <SidebarOverlay $isOpen={sidebarCollapsed} />

      <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarHeader>
          <LogoIcon />
          <SidebarSubtitle>Admin Panel</SidebarSubtitle>
        </SidebarHeader>

        <SidebarNav>
          {navItems.map((item) => {
            const isActive =
              pathname === item.to ||
              (item.id === "bands" &&
                pathname.startsWith("/admin-panel/bend")) ||
              (item.id === "news" &&
                pathname.startsWith("/admin-panel/novost"));

            const showAlert =
              (item.id === "products" && hasLowStockAlerts) ||
              (item.id === "orders" && hasPendingOrders);

            return (
              <NavItem
                key={item.id}
                $active={isActive}
                onClick={() => changePage(item.to)}
              >
                <NavIcon>
                  <item.icon />
                  {showAlert && <AlertDot />}
                </NavIcon>
                <NavText>{item.label}</NavText>
              </NavItem>
            );
          })}

          <BottomNav>
            <NavSupport
              $active={pathname === "/admin-panel/korisnicka-podrska"}
              onClick={() => {
                navigate("/admin-panel/korisnicka-podrska");
                setTimeout(() => {
                  window.scrollTo(0, 0);
                }, 300);
              }}
            >
              <NavIcon>
                <BiSupport />
              </NavIcon>
              Korisnička podrška
            </NavSupport>
            <NavLogout onClick={handleLogout}>
              <NavIcon>
                <FiLogOut />
              </NavIcon>
              Odjavi se
            </NavLogout>
          </BottomNav>
        </SidebarNav>
      </Sidebar>
      {!hideContent && (
        <MainContent $expanded={sidebarCollapsed}>
          <Header>
            <HeaderActions>
              <UserInfo>
                <FiUser />
                <span>Admin</span>
              </UserInfo>
            </HeaderActions>
          </Header>
          <Content>{children}</Content>
        </MainContent>
      )}
    </Container>
  );
};

export default AdminContainer;

const Container = styled.div`
  flex-direction: column;
  display: flex;
  min-height: 100vh;
`;
const BottomNav = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #333;
  margin-top: 1rem;
`;
const Sidebar = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 280px;
  height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%);
  border-right: 2px solid #ff6b35;
  z-index: 1000;
  transition: transform 0.3s ease;
  overflow-y: auto;
  transform: ${(props) =>
    props.$collapsed ? "translateX(-220px)" : "translateX(0)"};
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 250px;

    transform: ${(props) =>
      props.$collapsed ? "translateX(0)" : "translateX(-100%)"};
  }
`;

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(8px);
  z-index: 999;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (min-width: 769px) {
    display: none;
  }
`;

const SidebarToggle = styled.button`
  position: absolute;
  top: 1rem;
  left: -100%;
  background: #ff6b35;
  color: #ffffff;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 0 5px 5px 0;
  z-index: 1000;
  cursor: pointer;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e55a2b;
  }
  @media (max-width: 768px) {
    left: ${(props) => (!props.$collapsed ? "0" : "250px")};
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid #333;
  flex-direction: column;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
    padding: 1rem;
    flex-direction: row;
  }
`;

const SidebarSubtitle = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
`;
const LogoIcon = styled(Logo)`
  width: 100px;
  height: 100px;

  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
  }
`;

const SidebarNav = styled.nav`
  padding: 1rem 0;
  display: flex;
  height: 100%;
  flex-direction: column;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: ${(props) => (props.$active ? "#ff6b35" : "#cccccc")};
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid
    ${(props) => (props.$active ? "#ff6b35" : "transparent")};
  cursor: pointer;
  background: ${(props) =>
    props.$active ? "rgba(255, 107, 53, 0.1)" : "transparent"};
  position: relative;

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
    border-left-color: #ff6b35;
  }
`;

const NavIcon = styled.div`
  margin-right: 1rem;
  font-size: 1.2rem;
  width: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
`;

const NavText = styled.span`
  font-weight: 500;
`;

const AlertDot = styled.div`
  position: absolute;
  top: 0.65rem;
  transform: translateY(-50%);
  right: 0;
  width: 8px;
  height: 8px;
  background: #e74c3c;
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0px 0px 5px transparent;
    }
    50% {
      transform: scale(1.2);
      box-shadow: 0px 0px 5px #e74c3c;
    }
    100% {
      transform: scale(1);
      background: #e74c3c;
    }
  }
`;

const MainContent = styled.div`
  padding-left: ${(props) => (props.$expanded ? "60px" : "280px")};
  opacity: ${(props) => (!props.$expanded ? 1 : 0)};
  min-height: 100vh;
  transition: padding-left 0.3s ease-in-out, opacity 0.3s ease-in-out;
  width: 100%;

  @media (max-width: 768px) {
    padding-left: ${(props) => (!props.$expanded ? "0" : "250px")};
  }
  @media (max-width: 555px) {
    padding-left: ${(props) => (!props.$expanded ? "0" : "100%")};
  }
`;

const Header = styled.header`
  background: linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: flex-end;
  height: 57px;
  align-items: center;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #cccccc;
`;

const Content = styled.div`
  padding: 20px;
`;

const NavLogout = styled.div`
  display: flex;

  align-items: center;
  padding: 1rem 1.5rem;
  color: ${(props) => (props.$active ? "#ff073a" : "#cccccc")};
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid
    ${(props) => (props.$active ? "#ff073a" : "transparent")};
  cursor: pointer;
  background: ${(props) =>
    props.$active ? "rgba(255, 7, 58, .1))" : "transparent"};
  position: relative;

  &:hover {
    background: rgba(255, 7, 58, 0.1);
    color: #ff073a;
    border-left-color: #ff073a;
  }
`;

const NavSupport = styled.div`
  display: flex;

  align-items: center;
  padding: 1rem 1.5rem;
  color: ${(props) => (props.$active ? "#2196f3" : "#cccccc")};
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid
    ${(props) => (props.$active ? "#2196f3" : "transparent")};
  cursor: pointer;
  background: ${(props) =>
    props.$active ? "rgba(33, 150, 243, 0.1)" : "transparent"};
  position: relative;

  &:hover {
    background: rgba(33, 150, 243, 0.1);
    color: #2196f3;
    border-left-color: #2196f3;
  }
`;
